import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from '@/lib/auth';
import { LoadingState } from '@/components/AsyncState';
import '@/index.css';

const AppShell = lazy(() => import('@/components/AppShell'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const ChangePasswordPage = lazy(() => import('@/pages/ChangePasswordPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const TasksPage = lazy(() => import('@/pages/TasksPage'));
const CreateTaskPage = lazy(() => import('@/pages/CreateTaskPage'));
const TaskDetailPage = lazy(() => import('@/pages/TaskDetailPage'));

function PageLoading() {
  return <div className="route-loading"><LoadingState label="Opening Tasks…" /></div>;
}

function Protected({ children }) {
  const { loading, user } = useAuth();
  if (loading) return <PageLoading />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function LoginRoute() {
  const { loading, user } = useAuth();
  if (loading) return <PageLoading />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <LoginPage />;
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        <Route path="/login" element={<LoginRoute />} />
        <Route
          path="/change-password"
          element={<Protected><ChangePasswordPage /></Protected>}
        />

        <Route element={<Protected><AppShell /></Protected>}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/tasks/new" element={<CreateTaskPage />} />
          <Route path="/tasks/:taskId" element={<TaskDetailPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster
          position="top-right"
          richColors
          toastOptions={{
            style: {
              background: '#121916',
              border: '1px solid #25302c',
              color: '#f0f0f1',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}
